<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from "vue-router";
import type { INav } from "../types.ts";
import type { Component } from "vue";
import { useAuthStore } from '@/store/auth'
import { useExpertStore } from '@/store/expert'
import { useClientAuthStore } from '@/app/stores/clientAuth'
import { generateInitialsAvatar, handleImgError } from '@/utils/helpers'
import UserProfileCard from '@/components/expert/UserProfileCard.vue'
import ExpertWalletSidebarPreview from '@/components/expert/wallet/ExpertWalletSidebarPreview.vue'
import { useFeatureFlags } from '@/app/composables/useFeatureFlags'
import { UiText, UiIcon } from '@shopexperts/shared'
import { loadedAppVersion } from '@/generated/appVersion'
import SwitchOn from '@/assets/icons/switch-on.svg'

const props = defineProps<{
  menuItems: INav[];
  isExpertSideNav?: boolean;
  compact?: boolean;
  mobileMenuItems?: INav[];
  mobileLogoutIcon?: string | Component;
}>()

const mobileMenuOpen = defineModel<boolean>('mobileMenuOpen');

const emit = defineEmits<{
  'logout': [],
  'switchDashboard': []
}>()

const authStore = useAuthStore()
const expertStore = useExpertStore()
const clientAuthStore = useClientAuthStore()
const route = useRoute()
const { isExpertWalletEnabled } = useFeatureFlags()

const showWalletPreview = computed(() => Boolean(props.isExpertSideNav) && isExpertWalletEnabled(route.query))

// Client profile computed properties (from store)
const clientAvatarInfo = computed(() => generateInitialsAvatar(clientAuthStore.fullName || 'User'))

const activeItem = computed(() => {
  const allItems = [...props.menuItems, ...(props.mobileMenuItems || [])]
  const match = allItems.find(item => route.path.startsWith(item.path))
  return match ? match.label : null
})

const hasGroups = computed(() => props.menuItems.some(item => item.group))

const groupedItems = computed(() => {
  if (!hasGroups.value) return null
  const groups: { label: string; items: INav[] }[] = []
  const groupMap = new Map<string, INav[]>()
  const groupOrder: string[] = []

  for (const item of props.menuItems) {
    const key = item.group || ''
    if (!groupMap.has(key)) {
      groupMap.set(key, [])
      groupOrder.push(key)
    }
    groupMap.get(key)!.push(item)
  }

  for (const key of groupOrder) {
    groups.push({ label: key, items: groupMap.get(key)! })
  }
  return groups
})

const versionLabel = computed(() => `v${loadedAppVersion.version}`)

// Local state for expert data
const loading = ref(false)
const expertUser = ref<any>(null)

// Fetch expert settings data
const fetchExpertSettings = async () => {
  try {
    loading.value = true
    const response = await expertStore.fetchExpertProfile('');
    expertUser.value = response.expert;
    
    // Update auth store user with fresh data if needed
    if (response.expert) {
      authStore.user = { ...authStore.user, ...expertUser.value }
    }
  } catch (error) {
    console.error('Failed to fetch expert settings:', error)
    // Fallback to auth store user if API fails
    expertUser.value = authStore.user
  } finally {
    loading.value = false
  }
}

// Watch for mobile menu opening to fetch data
watch(mobileMenuOpen, (isOpen) => {
  if (isOpen && props.isExpertSideNav) {
    // Fetch fresh data every time the menu opens (like ExpertProfileDropdown)
    fetchExpertSettings()
  }
})

function closeMobileMenu() {
  if (mobileMenuOpen) {
    mobileMenuOpen.value = false
  }
}

function handleLogout() {
  emit('logout')
  closeMobileMenu()
}

function handleSwitchDashboard() {
  emit('switchDashboard')
  closeMobileMenu()
}

const expandAccountInfo = ref(false)
</script>

<template>
  <aside 
    class="relative xl:flex flex-col h-screen xl:h-full xl:min-h-0 bg-white font-sans"
    :class="{
      'flex fixed inset-0 z-50 w-screen top-[var(--safe-area-top,0px)] bottom-0 overflow-y-auto max-h-[calc(100dvh-var(--safe-area-top,0px))] se-scroll-lock': mobileMenuOpen,
      'hidden': !mobileMenuOpen,
    }"
  >
    <div
      class="flex min-h-0 flex-1 flex-col justify-start xl:justify-between"
      :class="compact ? 'p-4 xl:px-2 xl:py-5' : 'p-7'"
    >
      <!-- Expert User Profile Card - Mobile Only -->
      <div v-if="isExpertSideNav && mobileMenuOpen" class="xl:hidden mb-4">
        <!-- Loading State -->
        <div v-if="loading" class="w-full flex justify-center items-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
        
        <!-- User Profile Card -->
        <UserProfileCard 
          v-else-if="expertUser"
          :expert-user="expertUser" 
          :expand-account-info="expandAccountInfo"
          @toggle-expand="expandAccountInfo = !expandAccountInfo"
        />

        <!-- Expert wallet preview - Mobile, below the expert details -->
        <div v-if="showWalletPreview" class="mt-4">
          <ExpertWalletSidebarPreview variant="mobile" />
        </div>
      </div>

      <!-- Client User Profile Card - Mobile Only -->
      <div v-if="!isExpertSideNav && mobileMenuOpen" class="xl:hidden mb-4">
        <div class="w-full p-2 border border-grey rounded-md">
          <div class="flex items-center space-x-4">
            <div class="w-16 h-16 bg-light-apricot rounded-full flex items-center justify-center overflow-hidden">
              <!-- Show actual image if profile picture exists -->
              <img
                v-if="clientAuthStore.profilePicture"
                :src="clientAuthStore.profilePicture"
                alt="Profile"
                class="w-full h-full object-cover"
                @error="handleImgError"
              />
              <!-- Show initials avatar if no photo -->
              <div
                v-else
                :class="[
                  'w-full h-full rounded-full flex items-center justify-center text-white font-semibold text-h3',
                  clientAvatarInfo.bgColor
                ]"
              >
                {{ clientAvatarInfo.initials }}
              </div>
            </div>

            <!-- Profile Info -->
            <div>
              <h3 class="text-h2 font-semibold">{{ clientAuthStore.displayName }}</h3>
              <p v-if="clientAuthStore.client?.company_name" class="text-h4 text-grey-dark font-normal">
                {{ clientAuthStore.client.company_name }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <nav
        class="flex min-h-0 flex-1 flex-col overflow-y-auto"
        :class="compact ? 'gap-0.5' : 'gap-4'"
      >
        <!-- Grouped layout (desktop groups with headings) -->
        <template v-if="groupedItems">
          <div
            v-for="(group, gi) in groupedItems"
            :key="group.label"
            class="flex flex-col gap-px"
            :class="gi > 0 ? 'mt-5 xl:mt-6' : ''"
          >
            <span
              v-if="group.label"
              class="hidden xl:block px-2 pb-1 mb-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400/80 select-none"
            >{{ group.label }}</span>

            <slot v-for="item in group.items" :key="item.label" :name="`nav-${item.label.toLowerCase()}`" :close="closeMobileMenu">
            <component
              :is="item?.isLinkOnly ? 'a' : 'router-link'"
              :href="item?.isLinkOnly ? item.path : null"
              :to="!item?.isLinkOnly ? item.path : null"
              :target="item?.isLinkOnly ? '_blank' : null"
              class="flex items-center cursor-pointer transition-colors duration-100"
              :class="[
                compact
                  ? 'gap-2 min-h-8 px-2 rounded-md text-h5 items-center hover:bg-gray-50'
                  : 'gap-3 p-2 rounded-sm hover:bg-gray-100',
                activeItem === item.label
                  ? (compact ? 'bg-se-gray-100 font-semibold text-se-dark' : 'bg-secondary border')
                  : 'text-se-gray-700',
                item.disabled ? 'cursor-not-allowed opacity-40 pointer-events-none' : '',
              ]"
              @click="closeMobileMenu"
            >
              <span
                v-if="compact"
                class="flex w-5 h-5 shrink-0 self-center items-center justify-center"
                :class="activeItem === item.label ? 'opacity-100' : 'opacity-60'"
              >
                <UiIcon v-if="typeof item.icon === 'string'" :name="item.icon as any" class="w-[18px] h-[18px]" />
                <component v-else :is="item.icon" class="w-[18px] h-[18px] overflow-visible" />
              </span>
              <template v-else>
                <UiIcon v-if="typeof item.icon === 'string'" :name="item.icon as any" class="w-5 h-5" />
                <component v-else :is="item.icon" class="w-5 h-5" />
              </template>
              <UiText
                as="span"
                variant="override"
                :text="item.label"
                :class="compact ? 'flex min-h-5 items-center leading-none' : ''"
              />
            </component>
            </slot>
          </div>
        </template>

        <!-- Flat layout (original behavior) -->
        <template v-else>
          <slot v-for="item in menuItems" :key="item.label" :name="`nav-${item.label.toLowerCase()}`" :close="closeMobileMenu">
            <component
              :is="item?.isLinkOnly ? 'a' : 'router-link'"
              :href="item?.isLinkOnly ? item.path : null"
              :to="!item?.isLinkOnly ? item.path : null"
              :target="item?.isLinkOnly ? '_blank' : null"
              class="flex items-center cursor-pointer transition-colors duration-100"
              :class="[
                compact
                  ? 'gap-2 min-h-8 px-2 rounded-md text-h5 items-center hover:bg-gray-50'
                  : 'gap-3 p-2 rounded-sm hover:bg-gray-100',
                activeItem === item.label
                  ? (compact ? 'bg-se-gray-100 font-semibold text-se-dark' : 'bg-secondary border')
                  : 'text-se-gray-700',
                item.disabled ? 'cursor-not-allowed opacity-50 hover:bg-white pointer-events-none' : '',
              ]"
              @click="closeMobileMenu"
          >
            <span
              v-if="compact"
              class="flex w-5 h-5 shrink-0 self-center items-center justify-center"
              :class="activeItem === item.label ? 'opacity-100' : 'opacity-60'"
            >
              <UiIcon v-if="typeof item.icon === 'string'" :name="item.icon as any" class="w-[18px] h-[18px]" />
              <component v-else :is="item.icon" class="w-[18px] h-[18px] overflow-visible" />
            </span>
            <template v-else>
              <UiIcon v-if="typeof item.icon === 'string'" :name="item.icon as any" class="w-5 h-5" />
              <component v-else :is="item.icon" class="w-5 h-5" />
            </template>
            <UiText
              as="span"
              variant="override"
              :text="item.label"
              :class="compact ? 'flex min-h-5 items-center leading-none' : ''"
            />
          </component>
            </slot>
        </template>

        <!-- Mobile-only menu items -->
        <template v-if="mobileMenuItems && mobileMenuItems.length > 0">
          <div class="xl:hidden border-t border-gray-200 my-2"></div>
          
          <!-- Switch Dashboard Button - Expert Mobile Only -->
          <button
              v-if="isExpertSideNav"
              @click="handleSwitchDashboard"
              class="xl:hidden flex items-center gap-3 p-2 rounded-sm cursor-pointer hover:bg-gray-100"
          >
            <UiIcon name="overview" class="w-5 h-5" />
            <div class="flex w-full justify-between items-center">
              <UiText as="span" variant="override" text="New Dashboard" />
              <div class="relative w-[46px] h-[24px]">
                <SwitchOn class="absolute inset-0 opacity-100" />
              </div>
            </div>
          </button>

          <component
              v-for="item in mobileMenuItems"
              :key="item.label"
              :is="item?.isLinkOnly ? 'a' : 'router-link'"
              :href="item?.isLinkOnly ? item.path : null"
              :to="!item?.isLinkOnly ? item.path : null"
              :target="item?.isLinkOnly ? '_blank' : null"
              class="xl:hidden flex items-center gap-3 p-2 rounded-sm cursor-pointer hover:bg-gray-100 relative"
              :class="{ 'bg-secondary border': activeItem === item.label, 'cursor-not-allowed opacity-50 hover:bg-white pointer-events-none': item.disabled }"
              @click="closeMobileMenu"
          >
            <UiIcon v-if="typeof item.icon === 'string'" :name="item.icon as any" class="w-5 h-5" />
            <component v-else :is="item.icon" class="w-5 h-5" />
            <UiText as="span" variant="override" :text="item.label" />
            <span 
              v-if="item.badge && item.badge > 0"
              class="ml-auto w-5 h-5 text-[11px] bg-red-500 text-white rounded-full flex items-center justify-center"
            >
              {{ item.badge }}
            </span>
          </component>

          <button
              v-if="mobileLogoutIcon"
              @click="handleLogout"
              class="xl:hidden flex items-center gap-3 p-2 rounded-sm cursor-pointer hover:bg-gray-100"
          >
            <UiIcon v-if="typeof mobileLogoutIcon === 'string'" :name="mobileLogoutIcon as any" class="w-5 h-5" />
            <component v-else :is="mobileLogoutIcon" class="w-5 h-5" />
            <UiText as="span" variant="override" text="Logout" />
          </button>
        </template>
      </nav>

      <!-- Expert wallet preview - Desktop only, pinned to the bottom of the sidebar -->
      <div v-if="showWalletPreview" class="mt-4 hidden shrink-0 px-2 xl:block">
        <ExpertWalletSidebarPreview variant="sidebar" />
      </div>
    </div>

    <!-- App version - faint overlay in the bottom-right corner -->
    <span class="pointer-events-none absolute bottom-1.5 right-2 hidden text-[10px] leading-none text-se-gray-300 select-none xl:block">
      {{ versionLabel }}
    </span>
  </aside>
</template>
